'use client';

import { useState } from 'react';
import { redirect } from 'next/navigation';
import {
  PayPalScriptProvider,
  PayPalButtons as PayPalButtonsSdk,
} from '@paypal/react-paypal-js';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { Spinner } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import type { PaypalRecipientStatus, ISubscribedInfo } from 'types/src/types';

type Props = {
  creatorId: number;
  tierKey: string;
  creatorName: string;
  price?: number;
  isAnonymous?: boolean;
  isMyProfile: boolean;
  isSubscribe?: boolean;
  setIsSubscribe?: (v: boolean) => void;
  subscribedInfo?: ISubscribedInfo;
  paypalStatus?: PaypalRecipientStatus | null;
  className?: string;
};

export function SubscribePayPal({
  creatorId,
  tierKey,
  creatorName,
  price = 0,
  isAnonymous = false,
  isMyProfile,
  isSubscribe,
  setIsSubscribe,
  subscribedInfo,
  paypalStatus,
  className,
}: Props) {
  const isOpenDialog = useBoolean(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const canPayPalSubscribe =
    paypalStatus === 'email_verified' || paypalStatus === 'active';

  const handleClick = () => {
    if (isAnonymous) redirect('/sign-in');
    isOpenDialog.onTrue();
  };

  const clientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID!;

  const options = {
    clientId,
    currency: 'USD',
    intent: 'subscription',
    vault: true,
    components: 'buttons',
  } as const;

  return (
    <>
      <Button
        size="sm"
        className={cn('w-[50%]', className)}
        onClick={handleClick}
        disabled={
          isMyProfile ||
          !!isSubscribe ||
          subscribedInfo?.cancelAtPeriodEnd ||
          !canPayPalSubscribe
        }
      >
        {isSubscribe ? 'Subscribed' : 'Subscribe'}
      </Button>

      {isOpenDialog.value && (
        <div className="fixed inset-0 z-60 flex items-center justify-center h-screen w-screen">
          <div className="bg-white p-5 rounded-2xl shadow-md md:w-[540px] w-[95%] flex flex-col">
            <div className="flex flex-col gap-2">
              <h3 className="text-2xl! font-semibold text-center">
                Subscribe to {creatorName}
              </h3>
              <p className="text-black/60 text-sm font-light">
                Unlock access to all posts from this creator. You’ll be
                redirected to PayPal to complete the payment.
              </p>
            </div>

            <div className="font-semibold text-[40px] flex items-center">
              ${price}
              <span className="font-light text-sm text-black/60 pl-2">
                /month
              </span>
            </div>

            <hr className="w-full border-gray-100 my-3" />

            {!canPayPalSubscribe ? (
              <div className="text-sm text-black/60">
                PayPal subscriptions are not available for this creator yet.
              </div>
            ) : (
              <div className="relative">
                <PayPalScriptProvider options={options}>
                  <PayPalButtonsSdk
                    style={{ layout: 'vertical' }}
                    disabled={isProcessing}
                    createSubscription={async () => {
                      try {
                        setIsProcessing(true);
                        const res = await fetch(
                          '/api/paypal/subscriptions/create',
                          {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ creatorId, tierKey }),
                          },
                        );

                        const json = await res.json();
                        if (
                          !res.ok ||
                          !json?.ok ||
                          !json?.paypalSubscriptionId
                        ) {
                          throw new Error(
                            json?.error || 'Failed to create subscription',
                          );
                        }
                        return json.paypalSubscriptionId as string;
                      } finally {
                        setIsProcessing(false);
                      }
                    }}
                    onApprove={async (data) => {
                      setIsSubscribe?.(true);
                      isOpenDialog.onFalse();
                      setIsProcessing(false);
                    }}
                    onCancel={() => {
                      setIsProcessing(false);
                    }}
                    onError={(e) => {
                      console.error(e);
                      setIsProcessing(false);
                    }}
                  />
                </PayPalScriptProvider>

                {isProcessing && (
                  <div className="mt-2 flex items-center justify-center gap-2 text-sm text-black/60">
                    <Spinner /> Opening PayPal…
                  </div>
                )}
              </div>
            )}

            <hr className="w-full border-gray-100 my-3" />

            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                size="sm"
                className="bg-transparent px-4 h-7"
                onClick={isOpenDialog.onFalse}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
